import React, { useEffect, useState } from "react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CurrencyInput,
  Form,
  Typography,
  useWizard,
} from "@picklebet/ui";
import { walletService } from "@/features/wallet/services/wallet";
import { SupportLink } from "@/features/shell/components/shared/SupportLink";
import { useBlockingCardVerification } from "@/features/wallet/components/withdraw/hooks/useBlockingCardVerification";
import { useAudWithdrawEntryStep } from "@/features/wallet/components/withdraw/hooks/useWithdrawInitialStep";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import { MethodRow } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/MethodRow";
import { CardIcon } from "@/features/wallet/components/deposit/components/deposit-wizard/card-icon/CardIcon";

import * as styles from "./CardVerificationStep.module.scss";

const schema = z.object({
  amount: z
    .number({ error: "Please enter a value between $0.01 and $0.99" })
    .min(0.01, "Please enter a value between $0.01 and $0.99"),
});

export const CardVerificationStep = () => {
  const { currency } = useWithdrawWizard();
  const { goTo } = useWizard<WithdrawStepName>();
  const { verification, blockingCount } = useBlockingCardVerification();
  const { step: entryStep, isLoading: entryStepLoading } =
    useAudWithdrawEntryStep();
  const [requesting, setRequesting] = useState(false);

  const { control, handleSubmit, formState, reset } = useForm({
    defaultValues: { amount: undefined },
    resolver: zodResolver(schema),
  });

  const isExpired = verification?.expired === true;
  // cards still to verify once this one is done
  const remainingCardCount = blockingCount - 1;

  // the records are what put the user here, so they also decide when the gate
  // lifts — the last one clearing releases the user to wherever they would
  // have opened the modal on, which is bank details until one is saved
  useEffect(() => {
    if (verification || entryStepLoading) return;

    goTo(entryStep, { replace: true });
  }, [verification, entryStep, entryStepLoading, goTo]);

  // this step stays mounted while `verification` advances to the next card, so
  // the amount has to be cleared per card rather than on unmount
  useEffect(() => {
    reset({ amount: undefined });
  }, [verification?.id, reset]);

  if (!verification) {
    return null;
  }

  return (
    <WithdrawContent>
      <MethodRow
        icon={
          <CardIcon
            card={{
              expired: verification.expired ?? false,
              numberFirst: verification.numberFirst,
              numberLast: verification.numberLast,
            }}
          />
        }
        label={verification.name}
        sublabel={
          verification.expiryMonth &&
          verification.expiryYear &&
          `${verification.expiryMonth}/${verification.expiryYear}`
        }
      />

      <Typography variant={`body`} noSpacing>
        To withdraw you must verify the card(s) you deposited with.{" "}
        {remainingCardCount > 0 && (
          <>
            You have <em>{remainingCardCount}</em> card
            {remainingCardCount > 1 ? "s" : ""} left to verify after this card.
          </>
        )}
      </Typography>

      {verification.status === "Pending" && (
        <>
          <Typography variant={`body`} noSpacing>
            We'll charge a random amount between $0.01 and $0.99 to your card.
          </Typography>
          <Button
            disabled={isExpired || requesting}
            loading={requesting}
            onClick={async () => {
              if (isExpired) return;
              setRequesting(true);
              await walletService.beginVerification(verification.id);
              setRequesting(false);
            }}
          >
            {isExpired ? "Card Expired - Contact Support" : "Validate My Card"}
          </Button>
        </>
      )}

      {verification.status === "InProgress" &&
        (isExpired ? (
          <Typography variant="body" className={styles.danger} noSpacing>
            Card expired. Please contact Support.
          </Typography>
        ) : (
          <>
            <Typography variant={`body`} noSpacing>
              Check your bank statement for the exact amount charged, then enter
              it below.
            </Typography>
            <Form
              onSubmit={handleSubmit(async (values) => {
                await walletService.verifyCard(
                  verification.id,
                  values.amount.toFixed(2),
                );
              })}
              noSpacing
            >
              <Controller
                render={({ field }) => (
                  <CurrencyInput
                    {...field}
                    currency={currency}
                    errorMessage={formState?.errors?.amount?.message}
                    noSpacing
                    label={`Amount`}
                  />
                )}
                name={`amount`}
                control={control}
              />
              <Button loading={formState.isSubmitting} type={`submit`}>
                Submit Amount
              </Button>
            </Form>
            <Typography variant={`body`} noSpacing>
              <em>{verification.remainingAttempts}</em>{" "}
              {verification.remainingAttempts > 1 ? "attempts" : "attempt"}{" "}
              remaining. Don't guess the amount as 3 failed attempts will lock
              your account.
            </Typography>
            <Typography variant={`body`} noSpacing>
              Charges usually appear instantly, but can take up to 3 business
              days depending on your bank.
            </Typography>
          </>
        ))}

      {verification.status === "Failed" && (
        <>
          <Typography variant={`h4`} noSpacing>
            Verification Failed
          </Typography>
          <Typography variant={`body`} noSpacing>
            Your deposit limit has been set to $0. Contact Support to verify
            your card.
          </Typography>
          <SupportLink
            className={styles.button}
            type="button"
            openLiveChat={true}
          >
            Contact Support
          </SupportLink>
        </>
      )}
    </WithdrawContent>
  );
};
